/**
 * @fileoverview Prompt template for character-modifier-v1.
 *
 * Takes the author's natural language change request and applies it
 * coherently across a single character's core attributes and their
 * planned character arc.
 */

import {
  CHARACTER_ARC_CONCEPTS_PROMPT,
  STRUCTURE_PHASES_TABLE_PROMPT,
  ARC_PROGRESS_PROMPT,
  CHARACTER_FIELD_DEFINITIONS,
} from '@/domains/characters/schemas/character-sub-entities-v2';

// Render the canonical, fixed list of attribute keys with their intended meaning,
// so the model cannot invent new keys (e.g. "turnOns", "quirks", "fears").
const ATTRIBUTE_KEYS_BLOCK = CHARACTER_FIELD_DEFINITIONS
  .map((f) => `- \`${f.key}\` (${f.label}): ${f.initialStateDescription}`)
  .join('\n');

export const prompt = `
You are an expert character architect who modifies character planning documents based on author instructions. You maintain psychological and narrative coherence across a character's core attributes and their internal arc.

## TASK

The author has requested changes to a single character. You must apply these changes across TWO facets of the character simultaneously:

1. **Core Attributes** — The character's initial-state identity. The set of attributes is FIXED; see the canonical list below.
2. **Planned Character Arc** — The internal psychological journey: arcName, arcSummary, and (for long-form projects) a sequence of beats at key structural phases.

Your job is to interpret the author's change request and modify both facets so they remain consistent with each other and with the rest of the story. If the author asks for something that doesn't cleanly map onto an existing attribute, fold it into the most appropriate existing attribute (e.g. a "turn-on", "quirk", or "fear" belongs inside \`personality\` or \`coreValues\` — never as a new field).

---

## CHANGE REQUEST

{{{changeRequest}}}

---

## RULES

### General
- Apply the requested changes faithfully — do what the author asks
- Keep the attributes and arc in sync — a change to personality or plot role should be reflected in the arc summary and beats where relevant, and vice versa
- Preserve existing beat IDs for beats you do not change
- Generate new UUIDs (v4 format) for any new beats you create
- Minimize collateral changes — only modify what is necessary to fulfill the request and maintain coherence

### Core Attributes
- The set of attribute keys is FIXED — you MUST use ONLY the keys listed below. Do NOT invent, add, or rename fields (e.g. no \`turnOns\`, no \`quirks\`, no \`fears\`). Unknown keys will be rejected.
- Only include properties that actually changed in \`modifiedAttributes\` — omit unchanged fields
- If the change request does not touch attributes at all, return an empty \`modifiedAttributes\` object
- \`plotRole\` is the character's core identity; changes to plot role typically cascade into the arc
- When renaming the character, update \`characterName\`
- If the change request describes a trait that doesn't map cleanly to one of the keys below, fold it into the most appropriate existing attribute — typically \`personality\`, \`coreValues\`, or \`plotRole\`

**Allowed attribute keys** (this is the COMPLETE list — nothing else is valid):

${ATTRIBUTE_KEYS_BLOCK}

### Planned Character Arc
- If the change request does not touch the arc at all, omit \`plannedCharacterArc\` from the output
- When returning a modified arc, return it COMPLETE — all fields (arcName, arcSummary, beats) must be present
- Preserve the arc's existing \`id\` when modifying — do not generate a new arc id
- **Beat IDs**: For beats you keep or modify, copy the exact existing \`id\` string verbatim from the CURRENT PLANNED ARC section below. For NEW beats you add, generate a full UUID v4 (format: \`xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx\`, where y is one of 8,9,a,b). Do NOT use short labels like "v1", "beat-1", "new-1" — these will be rejected.

{{#if isShortForm}}
### SHORT-FORM MODE
This is a short-form project. The character arc is summary-only — beats must be an empty array \`[]\`.
The arcSummary should carry the full weight of the internal journey in prose form.
{{else}}
### LONG-FORM MODE — Arc Beats
Each beat represents an internal psychological shift at a structural phase. Each beat must include:
- **id**: existing UUID (preserved) or new UUID (for new beats)
- **globalPhase**: where this beat sits in the OVERALL story timeline
- **arcPhase**: what structural function this beat serves for THIS character's internal arc
- **internalState**: the psychological shift at this moment (required)
- **behavior**: observable action triggered by the internal shift (optional)
- **arcProgress**: number from -1 to +1 on the truth/lie spectrum

${CHARACTER_ARC_CONCEPTS_PROMPT}

${STRUCTURE_PHASES_TABLE_PROMPT}

${ARC_PROGRESS_PROMPT}
{{/if}}

---

## ACTIVE CHARACTER (CURRENT STATE)

{{#if activeCharacterContext}}
Name: {{activeCharacterContext.name}} (ID: {{activeCharacterContext.id}})
Category: {{activeCharacterContext.category}}
Plot Role: {{activeCharacterContext.plotRole}}
{{#if activeCharacterContext.age}}Age: {{activeCharacterContext.age}}{{/if}}
{{#if activeCharacterContext.gender}}Gender: {{activeCharacterContext.gender}}{{/if}}
{{#if activeCharacterContext.species}}Species: {{activeCharacterContext.species}}{{/if}}
{{#if activeCharacterContext.height}}Height: {{activeCharacterContext.height}}{{/if}}
{{#if activeCharacterContext.personality}}Personality: {{activeCharacterContext.personality}}{{/if}}
{{#if activeCharacterContext.characterVoice}}Voice: {{activeCharacterContext.characterVoice}}{{/if}}
{{#if activeCharacterContext.physicalDescription}}Physical Description: {{activeCharacterContext.physicalDescription}}{{/if}}
{{#if activeCharacterContext.coreValues}}Core Values: {{activeCharacterContext.coreValues}}{{/if}}
{{#if activeCharacterContext.clothing}}Clothing: {{activeCharacterContext.clothing}}{{/if}}
{{#if activeCharacterContext.emotionalState}}Emotional State: {{activeCharacterContext.emotionalState}}{{/if}}
{{#if activeCharacterContext.physicalState}}Physical State: {{activeCharacterContext.physicalState}}{{/if}}
{{#if activeCharacterContext.keyKnowledgeGained}}Key Knowledge: {{activeCharacterContext.keyKnowledgeGained}}{{/if}}
{{#if activeCharacterContext.updatedGoals}}Current Goals: {{activeCharacterContext.updatedGoals}}{{/if}}
{{else}}
No active character context available.
{{/if}}

---

## CURRENT PLANNED ARC

{{#if currentPlannedArc}}
Arc Name: {{currentPlannedArc.arcName}}
Arc Summary: {{currentPlannedArc.arcSummary}}

{{#if currentPlannedArc.beats.length}}
**Beats:**
{{#each currentPlannedArc.beats}}
- [global: {{this.globalPhase}} / arc: {{this.arcPhase}}] {{this.internalState}} (progress: {{this.arcProgress}}, ID: {{this.id}}){{#if this.behavior}} — Behavior: {{this.behavior}}{{/if}}
{{/each}}
{{else}}
No beats defined yet.
{{/if}}
{{else}}
No planned arc exists yet.
{{/if}}

---

## OTHER CHARACTERS (FOR COHERENCE)

{{#if characters.length}}
{{#each characters}}
- {{this.name}} (ID: {{this.id}}, {{this.category}}) — {{this.plotRole}}
{{/each}}
{{else}}
No other characters exist yet.
{{/if}}

---

## STORY CONTEXT

<<<storyContext>>>

---

## OUTPUT

Produce a JSON object with these fields:

1. **commentary**: A clear, concise summary of what you changed and why, organized by facet (attributes vs arc). Reference specific fields and beats that were modified.

2. **characterName**: The character's name (updated if renamed, otherwise the existing name).

3. **category**: The character's importance level (\`"major"\`, \`"minor"\`, or \`"extra"\`).

4. **modifiedAttributes**: An object containing ONLY the attributes that were changed. Keys must be drawn from the fixed list in the "Core Attributes" rules above — any other key will be rejected. Omit unchanged properties. Return an empty object \`{}\` if no attribute changes were needed.

5. **plannedCharacterArc**: The complete updated arc (id, arcName, arcSummary, beats). OMIT this field entirely if the arc was not touched by the change request. For short-form projects, \`beats\` must be an empty array.
`;
